// The paywall sheet.
//
// Task 6 ships the LAYOUT and the seam; Task 7 drops RevenueCat into it. The
// seam is `purchaseSlot`: when a caller passes real plan buttons they replace
// the fallback wholesale, so Task 7 adds a component and one prop instead of
// restructuring this file. With no slot (RC unconfigured, or not built yet) the
// modal renders the static proCopy prices as DISABLED "Available soon" buttons,
// which is the honest state - the plans are real, the checkout is not yet.
//
// Idiom is ShareDeckModal's: a hand-rolled fixed inset-0 backdrop, no portal and
// no dialog library. Backdrop click and Escape both close; the inner card stops
// propagation so a click inside never dismisses.
//
// Nothing in here is ever shown to an anonymous user: every trigger sits
// behind a resolved session.
//
// PORTALED TO <body>. MobileNav is `fixed bottom-0 z-50` and renders AFTER the
// routed page in App.tsx, so an in-place z-50 overlay loses to it on document
// order and the tab bar paints over the sheet and eats taps in that strip -
// the same trap DeckChatDock and NavMenu both document. This used to be
// masked: the only caller was inside DeckChatDock, which already portals its
// whole subtree. The account page's upgrade button is a caller that is not,
// so the portal belongs HERE, where every caller gets it.

import { useRef, type ReactNode } from 'react'
import { useModalFocus } from '../../hooks/useModalFocus'
import { createPortal } from 'react-dom'
import {
  paywallActivationNote,
  paywallAriaLabel,
  paywallBenefits,
  paywallCloseLabel,
  paywallPrivacyLabel,
  paywallSubtitle,
  paywallTermsLabel,
  paywallTitle,
  paywallUnavailableLabel,
  paywallUnavailableNote,
  planAnnualLabel,
  planAnnualNote,
  planMonthlyLabel,
  priceAnnualFallback,
  priceMonthlyFallback,
  PRIVACY_URL,
  TERMS_URL,
} from './proCopy'
import { OVERLAY_ABOVE_AD } from '../../lib/adInset'
import { purchaseSurface } from '../../lib/revenuecat'
import { finishDeckEnabled } from '../../lib/finishDeckFlag'

export interface ProUpgradeModalProps {
  open: boolean
  onClose: () => void
  /**
   * Task 7 seam. Renders in place of the disabled fallback plans, so the
   * RevenueCat-backed buttons (offerings as the price source, purchase flow,
   * restore/manage row) slot in without touching the rest of the sheet. Left
   * undefined the modal shows static prices with disabled buttons.
   */
  purchaseSlot?: ReactNode
  /**
   * The Pro tier's monthly message cap, from /pro/status `pro_cap_month`.
   * Undefined (no status yet, or an older server) renders the fallback.
   */
  proMonthlyMessages?: number
  /**
   * The caller opts in to the Finish my deck copy: true only when its
   * /pro/status offers Finish (proStatus finishOfferedByStatus) or it is
   * Finish's own sheet. Absent means NOT offered. The sheet names Finish
   * (subtitle and benefit) only when VITE_FINISH_DECK is on AND this is true.
   */
  finishOffered?: boolean
}

export default function ProUpgradeModal({ open, onClose, purchaseSlot, proMonthlyMessages, finishOffered }: ProUpgradeModalProps) {
  // Inside an Android app build that cannot sell (purchaseSurface() 'none')
  // the sheet never renders, whoever opens it: every caller (Deck Chat, Set
  // Check, the shared Paywall) goes through here, so this is the backstop for
  // a trigger that forgot its own check. A billing-capable build ('play')
  // shows it, with the Play hand-off panel in the slot (see Paywall).
  // eslint-disable-next-line i18next/no-literal-string -- purchase surface code
  const shown = open && purchaseSurface() !== 'none'
  // One layer of the shared modal stack (useModalFocus): top-only Escape and
  // Tab, focus in and back, covered while another layer is above. Bound only
  // while the sheet is actually shown.
  const layerRef = useRef<HTMLDivElement>(null)
  useModalFocus(shown, layerRef, onClose)

  if (!shown) return null
  const finish = finishDeckEnabled() && finishOffered === true

  return createPortal(
    <div
      ref={layerRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={paywallAriaLabel()}
      data-testid="pro-upgrade-modal"
      // Ends above the Mediavine adhesion ad, which stays in front by policy,
      // so the purchase buttons never sit under it (src/lib/adInset.ts).
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 ${OVERLAY_ABOVE_AD}`}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md surface rounded-2xl shadow-2xl overflow-hidden max-h-[calc(90vh-var(--ad-inset-bottom,0px))] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-800 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-white">{paywallTitle()}</h2>
            <p className="text-sm text-gray-400 mt-0.5">{paywallSubtitle(finish)}</p>
          </div>
          <button
            onClick={onClose}
            aria-label={paywallCloseLabel()}
            className="text-gray-400 hover:text-white transition-colors text-2xl leading-none shrink-0"
          >
            &times;
          </button>
        </div>

        <div className="px-5 py-5 space-y-5">
          <ul className="space-y-2" data-testid="pro-benefits">
            {paywallBenefits(proMonthlyMessages, finish).map((benefit) => (
              <li key={benefit} className="flex items-start gap-2 text-sm text-gray-200">
                <span className="text-purple-400 mt-0.5" aria-hidden>
                  &#10003;
                </span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          {purchaseSlot ?? <UnconfiguredPlans />}

          <p className="text-xs text-gray-500">{paywallActivationNote()}</p>

          <div className="border-t border-gray-800 pt-4 flex items-center gap-4">
            <a
              href={TERMS_URL}
              className="text-xs text-gray-500 hover:text-gray-300 underline transition-colors"
            >
              {paywallTermsLabel()}
            </a>
            <a
              href={PRIVACY_URL}
              className="text-xs text-gray-500 hover:text-gray-300 underline transition-colors"
            >
              {paywallPrivacyLabel()}
            </a>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * The no-RevenueCat state: both plans, real prices, no way to buy.
 *
 * Rendered whenever no purchaseSlot is supplied. Prices come from proCopy
 * FALLBACKS; once RC is configured its offerings are the price source and this
 * component is not rendered at all.
 */
function UnconfiguredPlans() {
  return (
    <div className="space-y-2" data-testid="pro-plans-unconfigured">
      <PlanButton
        // eslint-disable-next-line i18next/no-literal-string -- test id
        testId="pro-plan-monthly"
        label={planMonthlyLabel()}
        price={priceMonthlyFallback()}
      />
      <PlanButton
        // eslint-disable-next-line i18next/no-literal-string -- test id
        testId="pro-plan-annual"
        label={planAnnualLabel()}
        price={priceAnnualFallback()}
        note={planAnnualNote()}
      />
      <p className="text-xs text-gray-500 pt-1">{paywallUnavailableNote()}</p>
    </div>
  )
}

function PlanButton({
  testId,
  label,
  price,
  note,
}: {
  testId: string
  label: string
  price: string
  note?: string
}) {
  return (
    <button
      type="button"
      disabled
      data-testid={testId}
      className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-700 bg-gray-900/60 text-left opacity-60 cursor-not-allowed"
    >
      <span className="flex flex-col">
        <span className="text-sm font-medium text-gray-200">
          {label}
          {note && (
            <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-600/25 border border-purple-500/40 text-purple-200">
              {note}
            </span>
          )}
        </span>
        <span className="text-xs text-gray-400">{price}</span>
      </span>
      <span className="text-xs text-gray-400 shrink-0">{paywallUnavailableLabel()}</span>
    </button>
  )
}
